import store from '@/store';
import axios from 'axios';
import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';
import { Mutations, Endpoints, ID } from '@/ts/store/Constants';
import BuildingData from '@/ts/BuildingData';
import BuildingManagerData from '@/ts/BuildingManagerData';
import BuildingStaffData from '@/ts/BuildingStaffData';
import { BuildingDataResponse } from '@/ts/HococoInterfaces';
import Projects from './Projects';
import ErrorResponse from '@/ts/ErrorResponse';

@Module({name: 'Buildings', namespaced: true})
export default class Buildings extends VuexModule {
    public buildingId: number = null;
    public buildings: BuildingData[] = [];
    public buildingsMap: { [index: number]: number } = {};
    public cachedBuilding: BuildingData = null;
    public activeBuildingId: number = null;
    public managers: BuildingManagerData[] = [];
    public managersMap: { [index: number]: number } = {};
    public managerData: BuildingManagerData | null = null;
    public managerSuggestions: number[] = [];
    public managerSearchQuery: string = '';
    public staff: BuildingStaffData[] = [];
    public staffMap: { [index: number]: number } = {};
    public staffData: BuildingStaffData | null = null;
    public userSuggestionVisibility: boolean = false;
    public editFormVisible: boolean = false;

    @Mutation
    public RESET_BUILDINGS_STORE_STATE() {
        this.buildingId               = null;
        this.buildings                = [];
        this.buildingsMap             = {};
        this.cachedBuilding           = null;
        this.activeBuildingId         = null;
        this.managers                 = [];
        this.managersMap              = {};
        this.managerData              = null;
        this.managerSuggestions       = [];
        this.managerSearchQuery       = '';
        this.staff                    = [];
        this.staffMap                 = {};
        this.staffData                = null;
        this.userSuggestionVisibility = false;
        this.editFormVisible          = false;
    }

    get allBuildings(): BuildingData[] {
        return this.buildings.filter((b) => b.persisted);
    }

    get projectsBuildings() {
        return ((projectIds: number[]) => {
            return this.buildings.filter((building) => projectIds.includes(building.projectId));
        });
    }

    get buildingIds() {
        return this.buildings.map((building: BuildingData) => {
            return building.id;
        });
    }

    get getBuilding() {
        return ((buildingId: number) => {
            return this.buildings[this.buildingsMap[buildingId]];
        });
    }

    get allBuildingManagers() {
        return this.managers;
    }

    get allBuildingStaff() {
        return this.staff;
    }

    get selectedBuildings() {
        return this.buildings.filter((item) => item.selected);
    }

    get selectedBuildingIds() {
        return store.getters['Buildings/selectedBuildings']
            .map((building: BuildingData) => building.id);
    }

    get editMenuVisible(): boolean {
        return this.editFormVisible;
    }

    get activeBuilding() {
        if (this.activeBuildingId === null) {
            return null;
        }

        return this.buildings[this.buildingsMap[this.activeBuildingId]];
    }

    get getBuildingId() {
        if (this.activeBuildingId === null) {
            return null;
        }

        return this.buildings[this.buildingsMap[this.activeBuildingId]].id;
    }

    get getBuildingProject() {
        if (this.activeBuildingId === null) {
            return null;
        }

        for (const project of Projects.state.projects) {
            if (project.selected === true) {
                this.buildings[this.buildingsMap[this.activeBuildingId]].projectId = project.id;
            }
        }

        return this.buildings[this.buildingsMap[this.activeBuildingId]].projectId;
    }

    get getBuildingName() {
        if (this.activeBuildingId === null) {
            return null;
        }

        return this.buildings[this.buildingsMap[this.activeBuildingId]].name;
    }

    get getBuildingStaff() {
        if (this.activeBuildingId === null) {
            return null;
        }

        return this.buildings[this.buildingsMap[this.activeBuildingId]].staff;
    }

    get userSuggestionsVisible() {
        return this.userSuggestionVisibility;
    }

    get getManagerSuggestions() {
        return Buildings.state.managerSuggestions;
    }

    get getCacheManager() {
        return (id: number) => {
           return Buildings.state.managers[Buildings.state.managersMap[id]];
        };
    }

    get getCacheStaff() {
        return (id: number) => {
            // This doesn't do anything
            if (id === Buildings.state.staff.id) {
                return Buildings.state.staff;
            }

            return Buildings.state.staff[Buildings.state.staffMap[id]];
        };
    }

    get getManagerId() {
        return (Buildings.state.managerData !== null)
            ? Buildings.state.managerData.id
            : ID.NO_USER;
    }

    get getStaffId() {
        return (Buildings.state.staffData !== null)
            ? Buildings.state.staffData.id
            : ID.NO_USER;
    }

    @Action
    public INITIATE_BUILDING() {
        store.commit(Mutations.INITIATE_NEW_BUILDING);
    }

    @Action
    public FETCH_BUILDINGS() {
        axios.get(Endpoints.Admin.Buildings.BUILDINGS)
            .then((response) => {
                store.commit(Mutations.ADD_BUILDINGS, response.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
        return;
    }

    @Action
    public POST_BUILDINGS() {
        const requestPayload = new FormData();

        const building = Buildings.state.buildings[
                            Buildings.state.buildingsMap[
                                Buildings.state.activeBuildingId]];

        const requestData = {
            name: building.name,
            managers: building.managers,
            projectId: building.projectId,
        };

        axios.post(Endpoints.Admin.Buildings.BUILDINGS, requestData)
            .then((response) => {
                store.commit(Mutations.UPDATE_PERSISTED_BUILDING, response.data.id);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public EDIT_BUILDING() {
        const building = Buildings.state.buildings[
            Buildings.state.buildingsMap[
                Buildings.state.activeBuildingId]];

        if (building.dataEquals(Buildings.state.cachedBuilding)) {
            return store.commit(Mutations.HIDE_EDIT_BUILDING_FORM);
        }

        const requestData = {
            _method: 'PATCH',
            name: building.name,
            managers: building.managers,
        };

        axios.post(Endpoints.Admin.Buildings.SINGLE_BUILDING(building.id), requestData)
            .then((response) => {

            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public QUERY_MANAGER_SUGGESTIONS(searchQuery: string) {
        const requestData = {
            searchQuery,
        };

        const projectId = Buildings.state.buildings[
            Buildings.state.buildingsMap[
                Buildings.state.activeBuildingId]].projectId;

        if (!projectId) {
            return;
        }

        axios.post(Endpoints.Admin.Projects.GET_BUILDING_MANAGER_SUGGESTIONS(projectId), requestData)
            .then((response) => {
                store.commit(Mutations.ADD_BUILDING_MANAGER_SUGGESTIONS, response.data.suggestions);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public ADD_MANAGER_TO_BUILDING(managerId: number) {
        store.commit(Mutations.ADD_BUILDING_MANAGER, managerId);
        store.commit(Mutations.HIDE_USER_SUGGESTIONS);
        store.commit(Mutations.SET_MANAGER_SEARCH_QUERY, '');
    }

    @Action
    public ADD_STAFF_TO_BUILDING(staffId: number) {
        store.commit(Mutations.UPDATE_BUILDING_STAFF, staffId);
        store.commit(Mutations.HIDE_USER_SUGGESTIONS);
    }

    @Mutation
    public REMOVE_MANAGER_FROM_ACTIVE_BUILDING(managerId: number) {
        this.buildings[
            this.buildingsMap[
                this.activeBuildingId]].removeManager(managerId);
    }

    @Mutation
    public REMOVE_MANAGER_FROM_BUILDING(data: any) {
        this.buildings[
            this.buildingsMap[
                data.buildingId]].removeManager(data.managerId);
    }

    @Mutation
    public ADD_BUILDING(building: BuildingDataResponse): void {
        let arrayIndex = null;

        if (!this.buildingsMap.hasOwnProperty(building.id)) {
            const propertiesCount = building.numberOfProperties ? building.numberOfProperties.split(',') : [];

            arrayIndex = this.buildings.push(new BuildingData(
                building.id,
                building.name,
                propertiesCount.length,
                building.projectId,
            )) - 1;

            this.buildingsMap[building.id] = arrayIndex;

            if (building.associatedUsers !== null) {
                const associatedUsers = building.associatedUsers.split('|');

                for (const associatedUser of associatedUsers) {
                    const userData = associatedUser.split(':');

                    this.buildings[arrayIndex].addAuthorization(
                        parseInt(userData[0], 10),
                        parseInt(userData[1], 10),
                    );
                }
            }
        } else {
            const existingBuilding = this.buildings[this.buildingsMap[building.id]];

            if (existingBuilding) {
                existingBuilding.updateLocalBuildingData(building);
            }
        }
    }

    @Mutation
    public ADD_BUILDINGS(buildingData: BuildingDataResponse[]): void {
        if (!buildingData.length) {
            this.buildings    = [];
            this.buildingsMap = {};
        }

        for (const building of buildingData) {
            let arrayIndex = null;

            if (!this.buildingsMap.hasOwnProperty(building.id)) {
                const propertiesCount = building.numberOfProperties ? building.numberOfProperties.split(',') : [];

                arrayIndex = this.buildings.push(new BuildingData(
                    building.id,
                    building.name,
                    propertiesCount.length,
                    building.projectId,
                )) - 1;

                this.buildingsMap[building.id] = arrayIndex;
            } else {
                arrayIndex             = this.buildingsMap[building.id];
                const existingBuilding = this.buildings[arrayIndex];

                if (existingBuilding) {
                    existingBuilding.updateLocalBuildingData(building);
                }
            }

            if (building.associatedUsers !== null) {
                const associatedUsers = building.associatedUsers.split('|');
                const userObjects = associatedUsers.map((item) => {
                    const splitData = item.split(':');

                    return {
                        userId: parseInt(splitData[0], 10),
                        roleId: parseInt(splitData[1], 10),
                        firstName: splitData[2],
                        familyName: splitData[3],
                        phoneNumber: splitData[4],
                    };
                });

                store.commit(Mutations.ADD_USERS_TO_CACHE, userObjects);

                for (const associatedUser of userObjects) {
                    this.buildings[arrayIndex].addAuthorization(
                        associatedUser.userId,
                        associatedUser.roleId,
                    );
                }
            }
        }
    }

    @Mutation
    public UPDATE_BUILDING_NAME(value: string): void {
        this.buildings[this.buildingsMap[this.activeBuildingId]].name = value;
    }

    @Mutation
    public UPDATE_BUILDING_STAFF(value: number[]): void {
        this.buildings[this.buildingsMap[this.activeBuildingId]].staff = value;
    }

    @Mutation
    public UPDATE_BUILDING_PROJECT_ID(value: number): void {
        this.buildings[this.buildingsMap[this.activeBuildingId]].projectId = value;
    }

    @Mutation
    public ADD_BUILDING_MANAGER_SUGGESTIONS(buildingManager: any) {
        let arrayIndex = null;

        this.managerSuggestions = [];

        for (const manager of buildingManager) {
            if (!this.managersMap.hasOwnProperty(manager.user_id)) {
                arrayIndex = this.managers.push(new BuildingManagerData(
                    manager.user_id,
                    manager.first_name,
                    manager.family_name,
                    manager.role_id,
                )) - 1;

                this.managersMap[manager.user_id] = arrayIndex;
                this.managerSuggestions.push(manager.user_id);
            } else {
                arrayIndex = this.managersMap[manager.user_id];
                this.managerSuggestions.push(manager.user_id);
            }

            store.commit(Mutations.ADD_USER_TO_CACHE, {
                userId: manager.user_id,
                roleId: manager.role_id,
                firstName: manager.first_name,
                familyName: manager.family_name,
            });
        }

        return arrayIndex;
    }

    @Mutation
    public SHOW_BUILDING_USER_SUGGESTIONS(): void {
        this.userSuggestionVisibility = true;
    }

    @Mutation
    public HIDE_USER_SUGGESTIONS(): void {
        this.userSuggestionVisibility = false;
    }

    @Mutation
    public UPDATE_ACTIVE_BUILDING(buildingId: number): void {
        this.activeBuildingId = buildingId;
    }

    @Mutation
    public EDIT_ACTIVE_BUILDING(): void {
        this.cachedBuilding = JSON.parse(JSON.stringify(this.buildings[this.buildingsMap[this.activeBuildingId]]));
    }

    @Mutation
    public INITIATE_NEW_BUILDING() {
        const activeIndex = this.buildings.push(new BuildingData(-1)) - 1;
        this.buildingsMap[-1] = activeIndex;
        this.activeBuildingId = -1;
    }

    @Mutation
    public SHOW_EDIT_BUILDING_FORM() {
        this.editFormVisible = true;
    }

    @Mutation
    public HIDE_EDIT_BUILDING_FORM() {
        this.editFormVisible = false;
    }

    @Mutation
    public SELECT_BUILDING(itemId: number) {
        this.buildings[this.buildingsMap[itemId]].selected = true;
    }

    @Mutation
    public DESELECT_BUILDING(itemId: number) {
        this.buildings[this.buildingsMap[itemId]].selected = false;
    }

    @Mutation
    public RESET_BUILDING_SELECTION() {
        for (const building of this.buildings) {
            building.selected = false;
        }
    }

    @Mutation
    public SET_MANAGER_SEARCH_QUERY(value: string) {
        this.managerSearchQuery = value;
    }

    @Mutation
    public CANCEL_EDIT_BUILDING() {
        this.buildings[this.buildingsMap[this.activeBuildingId]].cloneData(this.cachedBuilding);

        this.cachedBuilding = null;
    }

    @Mutation
    public ADD_BUILDING_MANAGER(managerId: number) {
        this.buildings[this.buildingsMap[this.activeBuildingId]].addManager(managerId);
    }

    @Mutation
    public UPDATE_PERSISTED_BUILDING(newBuildingId: number) {
        const currentArrayIndex = this.buildingsMap[this.activeBuildingId];
        this.activeBuildingId = null;
        this.buildingsMap[newBuildingId] = currentArrayIndex;
        this.buildings[currentArrayIndex].id = newBuildingId;
        delete this.buildingsMap[-1];
    }
}

